import { Form, Link, Modal } from '@appsumo/dorado-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo, useRef } from 'react';
import reactStringReplace from 'react-string-replace';
import type { Deal } from '~/types/deal';
import { useRouter } from 'next/router';
import { getQueryFromPath } from '~/lib/util';
import { useSubmitReply } from '~/lib/util/hooks/submitReply';
import { YupQuestionSchema, YupReviewSchema } from '~/types/question-review';
import { useEventDispatch } from '~/lib/events';

type TextType = 'review' | 'comment' | 'reply';
const TextTypes: Record<
  TextType,
  {
    title: string;
    prompt: string;
    placeholder: string;
    notifyLabel: string;
    instructionTitle: string;
    instructions: string[];
  }
> = {
  review: {
    title: 'Submit a review',
    prompt: 'Review',
    placeholder: 'Enter your review',
    notifyLabel: 'Notify me via email when someone replies to this review',
    instructionTitle:
      'Please check these guidelines before submitting your review:',
    instructions: [
      "Please review the product you're purchasing, not the terms of the deal.",
      'Add value when leaving a review.',
      'Try the product before reviewing it!',
    ],
  },
  comment: {
    title: 'Submit a question',
    prompt: 'Your question',
    placeholder: 'Enter your question',
    notifyLabel: 'Notify me via email when someone replies to this question',
    instructionTitle:
      'Please check these guidelines before submitting your question:',
    instructions: [
      'Check the FAQ and pinned comments before you post, your question may have already been answered!',
      'Please don’t use this forum as a platform for requesting deal features or new terms.',
      'If you have an AppSumo support related issue, shoot us an email at ||support@appsumo.com||.',
      'Be respectful of your fellow Sumo-lings and partners – rudeness or harassment will not be tolerated.',
    ],
  },
  reply: {
    title: 'Reply to {username}',
    prompt: 'Your reply',
    placeholder: 'Enter your response',
    notifyLabel: 'Notify me via email for follow-ups',
    instructionTitle:
      'Please check these guidelines before submitting a review:',
    instructions: [
      'Please don’t use this forum as a platform for requesting deal features or new terms.',
      'If you have an AppSumo support related issue, shoot us an email at ||support@appsumo.com||.',
      'Be respectful of your fellow Sumo-lings and partners – rudeness or harassment will not be tolerated.',
    ],
  },
};

interface ReplyModalProps {
  comment?: any;
  type: 'dealcomment' | 'dealreview';
  deal: Deal;
  hideModal: Function;
  mutateComment: Function;
}

export function ReplyModalV2({
  comment,
  type,
  deal,
  hideModal,
  mutateComment,
}: Readonly<ReplyModalProps>) {
  const formRef = useRef<HTMLFormElement>(null);
  const dispatchEvent = useEventDispatch();
  const hideModalAndEmit = () => {
    hideModal();
    dispatchEvent('pdp:toggleBuyButton', false);
  };
  const { handleSubmit } = useSubmitReply({
    type,
    mutateComment,
    comment,
    dealId: deal.id,
    formRef,
    successCallback: hideModalAndEmit,
  });

  const isReply = !!comment;
  const isReview = type === 'dealreview';

  // Set initial rating from query param
  const { asPath } = useRouter();
  const params = getQueryFromPath(asPath);
  const rating = Number(params.get('rating')) || 5;
  const initialRating = isReply ? 0 : rating;

  const textType: TextType = useMemo(() => {
    if (isReply) {
      return 'reply';
    }

    if (isReview) {
      return 'review';
    }

    return 'comment';
  }, [isReview, isReply]);

  const modalTitle = useMemo(
    () =>
      TextTypes[textType].title.replace('{username}', comment?.user?.username),
    [textType, comment?.user?.username],
  );

  const instructions = useMemo(
    () =>
      TextTypes[textType].instructions.map((instruction, index) => (
        <li key={index}>
          {reactStringReplace(instruction, /\|\|([^|]+)/, (match, index) => (
            <a
              href={`mailto:${match}`}
              className="text-bolt hover:underline"
              key={index}
            >
              {match}
            </a>
          ))}
        </li>
      )),
    [textType],
  );

  useEffect(() => {
    const titleElement = document.querySelector(
      'input[name="title"]',
    ) as HTMLInputElement;
    setTimeout(
      () => titleElement?.focus({ focusVisible: true } as FocusOptions),
      0,
    );
  }, []);

  return (
    <Modal
      title={modalTitle}
      hideModal={hideModalAndEmit}
      actionButton={{
        label: 'Submit',
        callback: (ev: Event) => {
          ev.preventDefault();
          formRef?.current?.submit();
        },
      }}
      cancelButton={{ label: 'Cancel', callback: hideModalAndEmit }}
      className="max-h-[calc(100vh-4rem)] overflow-y-auto"
    >
      <Form
        ref={formRef}
        resolver={yupResolver(
          isReply || type === 'dealcomment'
            ? YupQuestionSchema
            : YupReviewSchema,
        )}
        onSubmit={handleSubmit}
        defaultValues={{
          rating: initialRating,
        }}
      >
        <div className="flex flex-col gap-y-4">
          {isReply && (
            <div className="rounded bg-iceberg p-4">
              {comment.title && (
                <p className="mb-4 font-bold">{comment.title}</p>
              )}
              <p>{comment.comment}</p>
            </div>
          )}
          {!isReply && isReview && (
            <div className="grid grid-cols-2 gap-x-4">
              <Form.Input
                name="title"
                label="Title"
                placeholder="Enter your title"
              />
              <Form.StarRating
                name="rating"
                label="Click to provide your taco rating"
              />
            </div>
          )}
          <div>
            <Form.TextArea
              name="comment"
              label={TextTypes[textType].prompt}
              placeholder={TextTypes[textType].placeholder}
            />
          </div>
          <div>
            <Form.Checkbox
              name="notify"
              label={TextTypes[textType].notifyLabel}
            />
          </div>
          <div className="rounded bg-iceberg p-4 text-sm">
            <h5 className="mb-2 font-bold">
              {TextTypes[textType].instructionTitle}
            </h5>
            <ul className="flex flex-col gap-y-2">{instructions}</ul>
          </div>
          <div className="text-sm">
            By submitting, I agree to{' '}
            <Link href="/community-guidelines/" styled>
              AppSumo&apos;s Community Guidelines
            </Link>
          </div>
        </div>
      </Form>
    </Modal>
  );
}
